import React, { useState } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Link } from 'react-router-dom';
import { PhoneIncoming, PhoneOutgoing, ArrowLeftRight, GitBranch, ChevronRight } from 'lucide-react';

export const DIRECTIONS = [
  { id: 'inbound', label: 'Inbound', desc: 'Answers incoming calls', icon: PhoneIncoming },
  { id: 'outbound', label: 'Outbound', desc: 'Makes outgoing calls', icon: PhoneOutgoing },
  { id: 'both', label: 'Both', desc: 'Inbound and outbound', icon: ArrowLeftRight },
  { id: 'ivr', label: 'IVR / Call Routing', desc: 'Greets callers and routes them', icon: GitBranch },
];

const HINTS = {
  inbound: 'Speaks first when it answers.',
  outbound: 'Waits for the person to say hello first.',
  both: 'Speaks first on inbound calls, waits on outbound calls.',
  ivr: 'Answers your line, offers your Call Routing menu and sends callers to the right place.',
};

export const CallDirectionPicker = ({ value, onChange, routingHref, hasTemplate, promptEmpty, onUseTemplate, textPrimary, textSecondary, inputBg, cardBorder }) => {
  const [confirm, setConfirm] = useState(false);
  return (
  <div data-testid="call-direction-picker">
    <label className={`block text-sm ${textSecondary} mb-2`}>Call Direction</label>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {DIRECTIONS.map(({ id, label, desc, icon: Icon }) => {
        const on = value === id;
        return (
          <button key={id} type="button" onClick={() => onChange(id)} data-testid={`direction-${id}`} aria-pressed={on}
            className={`group p-4 rounded-xl border text-left transition-colors ${on ? 'bg-teal-400/10 border-teal-400/40' : `${inputBg} ${cardBorder} hover:border-teal-400/30`}`}>
            <Icon size={18} className={`mb-2 transition-colors ${on ? 'text-teal-400' : textSecondary}`} />
            <p className={`font-medium text-sm ${on ? 'text-teal-400' : textPrimary}`}>{label}</p>
            <p className={`text-[11px] leading-snug ${textSecondary} mt-0.5`}>{desc}</p>
          </button>
        );
      })}
    </div>
    <p className={`mt-2.5 text-xs ${textSecondary}`} data-testid="direction-hint">{HINTS[value] || HINTS.both}</p>
    {value === 'ivr' && (
      <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 p-3.5 rounded-xl border border-teal-400/20 bg-teal-400/[0.06]" data-testid="ivr-direction-note">
        <p className={`text-xs ${textSecondary}`}>
          The <span className="text-teal-400 font-medium">Call Routing</span> tool is turned on. Menus and destinations are edited in Call Routing. Pick this agent in a flow's Start step.
        </p>
        <div className="shrink-0 flex items-center gap-3">
          {!hasTemplate && (
            <button type="button" onClick={() => (promptEmpty ? onUseTemplate() : setConfirm(true))} className="text-xs font-medium text-teal-400 hover:underline" data-testid="ivr-use-prompt-template">
              Use IVR prompt template
            </button>
          )}
          <Link to={routingHref} className="inline-flex items-center gap-1 text-xs font-medium text-teal-400 hover:underline" data-testid="ivr-open-call-routing">
            Open Call Routing <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    )}
    <AlertDialog open={confirm} onOpenChange={setConfirm}>
      <AlertDialogContent data-testid="ivr-template-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Replace the system prompt?</AlertDialogTitle>
          <AlertDialogDescription>Your current prompt will be replaced with the IVR / Call Routing template. You can edit it afterwards under Prompts.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ivr-template-cancel">Keep current</AlertDialogCancel>
          <AlertDialogAction onClick={onUseTemplate} data-testid="ivr-template-replace">Use template</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
  );
};

export default CallDirectionPicker;
